import { User } from 'firebase/auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { FileServiceProvider } from '@wonderschool/file-service-client';
import ResponsiveContainer from './Components/Layout/ResponsiveContainer';
import { auth } from './api/firebase/firebase';

import { startOrganizationContactsListener } from './redux/actions/contactsActions';
import { startOrganizationLocationsListener } from './redux/actions/locationActions';
import { getOrganizationSetupState, startOrganizationListener } from './redux/actions/organizationActions';
import { startOrganizationPermissionsListener } from './redux/actions/permissionActions';
import { startOrganizationRolesListener } from './redux/actions/roleActions';
import { startOrganizationRoomsListener } from './redux/actions/roomActions';
import { startOrganizationStatsListener } from './redux/actions/statsActions';
import { startOrganizationStudentsListener } from './redux/actions/studentsActions';
import { startOrganizationClockedInListener } from './redux/actions/timecardsActions';
import { startUserProfileListener, userSignedIn, userSignedOut } from './redux/actions/userActions';

import { isLocal } from './config/env';
import { CLAIMS_ENUM, isUsersHighestClaimOneOf } from './helpers/utils';
import { AppRouter } from './navigation';

import { IContextType } from '@wonderschool/common-base-sso/components/WSAuthProvider';
import WSA from './WSA';
import { identifyHotjar } from './hotjar';
import { identifyIntercom } from './intercom';
import { RootReducerState } from './redux/reducers/rootReducer';
import { trackNewSession, trackRollbarUser, trackUser, trackUserSignout } from './rollbar';

const FILE_SERVICE_HOST = import.meta.env.VITE_FILE_SERVICE_HOST;

type UnsubscribeType = () => any;

interface AppProps extends IContextType {
  currentOrganization: {
    id: string;
    setup: {
      firstLocationCreated: boolean;
      firstRoomCreated: boolean;
      firstStaffCreated: boolean;
      firstStudentCreated: boolean;
    };
    fetching: boolean;
    programType: string;
    setupComplete: boolean;
    stripeId: string;
  };
  organizationId: string;
  needsOrgOnboarding: boolean;
  canStartOrgListeners: boolean;
  userRooms?: Record<string, boolean>;
  userProfileOrganizationId?: string;
  userId: string;
  userSignedIn: (x: any, y: any) => any;
  userSignedOut: () => any;
  startUserProfileListener: (userId: string) => any;
  startOrganizationListener: (organizationId: string) => any;
  startOrganizationPermissionsListener: (organizationId: string) => any;
  startOrganizationRolesListener: (organizationId: string) => any;
  startOrganizationLocationsListener: (organizationId: string) => any;
  startOrganizationRoomsListener: (organizationId: string) => any;
  startOrganizationStatsListener: (organizationId: string) => any;
  startOrganizationStudentsListener: (organizationId: string, parentUserId?: string) => any;
  startOrganizationClockedInListener: (organizationId: string) => any;
  startOrganizationContactsListener: (organizationId: string) => any;
  forceUserRefresh: () => void;
}

function App({
  isReady,
  isAuthenticated,
  authUser,
  userId,
  authSession,
  userStatus,
  userClaims,
  userSignedIn,
  userSignedOut,
  organizationId,
  needsOrgOnboarding,
  canStartOrgListeners,
  userRooms,
  userProfileOrganizationId,
  startUserProfileListener,
  startOrganizationListener,
  startOrganizationPermissionsListener,
  startOrganizationRolesListener,
  startOrganizationLocationsListener,
  startOrganizationRoomsListener,
  startOrganizationStudentsListener,
  startOrganizationStatsListener,
  startOrganizationClockedInListener,
  startOrganizationContactsListener,
  forceUserRefresh,
}: AppProps) {
  const [hasPrevUser, setHasPrevUser] = useState(false);
  const [prevOrgId, setPrevOrgId] = useState<string | undefined>(undefined);
  const [prevClaims, setPrevClaims] = useState<any | undefined>(undefined);

  // Refs to store unsubscribe functions
  const unsubscribeRefs = useRef({
    userProfileListener: null as UnsubscribeType | null,
    organizationListener: null as UnsubscribeType | null,
    organizationPermissionsListener: null as UnsubscribeType | null,
    organizationRoleListener: null as UnsubscribeType | null,
    organizationLocationsListener: null as UnsubscribeType | null,
    organizationRoomsListener: null as UnsubscribeType | null,
    organizationStudentsListener: null as UnsubscribeType | null,
    organizationStatsListener: null as UnsubscribeType | null,
    organizationClockedInListener: null as UnsubscribeType | null,
    organizationContactsListener: null as UnsubscribeType | null,
  });

  const unsubscribeUserListeners = useCallback(() => {
    if (unsubscribeRefs.current.userProfileListener) {
      unsubscribeRefs.current.userProfileListener();
    }
  }, []);

  const unsubscribeOrganizationListeners = useCallback(() => {
    // The user profile listener uses a dual listener pattern (root user + org user documents)
    // and handles organization changes internally, so it should stay active across org switches
    Object.entries(unsubscribeRefs.current).forEach(([key, unsubscribe]) => {
      if (key !== 'userProfileListener' && unsubscribe) {
        unsubscribe();
      }
    });
  }, []);

  const startProfileListener = useCallback(
    (uid: string) => {
      unsubscribeRefs.current.userProfileListener = startUserProfileListener(uid);
    },
    [startUserProfileListener]
  );

  const startListeners = useMemo(
    () => ({
      organization: (id: string) => {
        unsubscribeRefs.current.organizationListener = startOrganizationListener(id);
      },
      permissions: (id: string) => {
        unsubscribeRefs.current.organizationPermissionsListener = startOrganizationPermissionsListener(id);
      },
      roles: (id: string) => {
        unsubscribeRefs.current.organizationRoleListener = startOrganizationRolesListener(id);
      },
      locations: (id: string) => {
        unsubscribeRefs.current.organizationLocationsListener = startOrganizationLocationsListener(id);
      },
      rooms: (id: string) => {
        unsubscribeRefs.current.organizationRoomsListener = startOrganizationRoomsListener(id);
      },
      students: (orgId: string, parentUserId?: string) => {
        unsubscribeRefs.current.organizationStudentsListener = startOrganizationStudentsListener(orgId, parentUserId);
      },
      stats: (id: string) => {
        unsubscribeRefs.current.organizationStatsListener = startOrganizationStatsListener(id);
      },
      clockedIn: (id: string) => {
        unsubscribeRefs.current.organizationClockedInListener = startOrganizationClockedInListener(id);
      },
      contacts: (id: string) => {
        unsubscribeRefs.current.organizationContactsListener = startOrganizationContactsListener(id);
      },
    }),
    [
      startOrganizationListener,
      startOrganizationPermissionsListener,
      startOrganizationRolesListener,
      startOrganizationLocationsListener,
      startOrganizationRoomsListener,
      startOrganizationStudentsListener,
      startOrganizationStatsListener,
      startOrganizationClockedInListener,
      startOrganizationContactsListener,
    ]
  );

  // Effect for claims changes and user tracking
  useEffect(() => {
    if (!isReady || !isAuthenticated) return;

    const claimsChanged = JSON.stringify(prevClaims) !== JSON.stringify(userClaims);
    if (claimsChanged) {
      setPrevClaims(userClaims);
      userSignedIn(authUser, userClaims);
      trackRollbarUser(authUser, organizationId);

      if (isLocal() && userId) {
        console.log('++++++++++++++++ Wonderschool Dev ******************');
        console.log('User Found: ', {
          authUser,
          userStatus,
          userSession: authSession,
          moxitAdmin: !!userClaims?.moxitAdmin,
          organizationAdmin: !!userClaims?.organizationAdmin,
          organizationDevice: !!userClaims?.organizationDevice,
          locationAdmin: !!userClaims?.locationAdmin,
          supportStaff: !!userClaims?.supportStaff,
          teacher: !!userClaims?.teacher,
          parent: !!userClaims?.parent,
          billingAccount: !!userClaims?.billingAccount,
        });
        console.log('++++++++++++++++ Wonderschool Dev ******************');
      }
    }

    if (userId) {
      identifyHotjar();
      identifyIntercom(authUser);
    }
  }, [
    isReady,
    isAuthenticated,
    userClaims,
    prevClaims,
    userId,
    authUser,
    userSignedIn,
    organizationId,
    userStatus,
    authSession,
  ]);

  // Effect for user listener setup
  useEffect(() => {
    if (!isReady || !isAuthenticated || !userId) return;

    // Handle user connection/disconnection
    if (!hasPrevUser) {
      setHasPrevUser(true);
      startProfileListener(userId);
    }
  }, [isReady, isAuthenticated, userId, hasPrevUser, startProfileListener]);

  // Effect for handling user disconnection
  useEffect(() => {
    if (!hasPrevUser || !isReady || isAuthenticated) return;

    if (isLocal()) {
      console.log('++++++++++++++++ Wonderschool Dev ******************');
      console.log('🛑 User Disconnected 🛑');
      console.log('++++++++++++++++ Wonderschool Dev ******************');
    }

    unsubscribeUserListeners();
    trackRollbarUser(null, null);
    userSignedOut();
    setHasPrevUser(false);
  }, [hasPrevUser, isReady, isAuthenticated, unsubscribeUserListeners, userSignedOut]);

  // Effect for organization updates - optimized dependencies
  useEffect(() => {
    if (!isAuthenticated || needsOrgOnboarding) return;

    const hasPrevOrg = !!prevOrgId;
    const needsFirstOrg = !hasPrevOrg && !!organizationId;
    const hasDifferentOrg = hasPrevOrg && prevOrgId !== organizationId;
    const needsOrgListeners = canStartOrgListeners && (needsFirstOrg || hasDifferentOrg);

    if (needsOrgListeners) {
      forceUserRefresh();
      setPrevOrgId(organizationId);
      unsubscribeOrganizationListeners();

      startListeners.organization(organizationId);
      startListeners.permissions(organizationId);
      startListeners.roles(organizationId);
      startListeners.locations(organizationId);
      startListeners.rooms(organizationId);

      const parentUserId = userClaims?.parent ? userId : undefined;
      startListeners.students(organizationId, parentUserId);
      startListeners.stats(organizationId);
      startListeners.clockedIn(organizationId);
      startListeners.contacts(organizationId);
    } else if (hasPrevOrg && (!isAuthenticated || !organizationId)) {
      unsubscribeOrganizationListeners();
      setPrevOrgId(undefined);
    }
  }, [
    isAuthenticated,
    organizationId,
    needsOrgOnboarding,
    canStartOrgListeners,
    prevOrgId,
    forceUserRefresh,
    startListeners,
    unsubscribeOrganizationListeners,
    userClaims,
    userId,
  ]);

  // Room-scoped listeners build their query from the organization user profile. The
  // initial listener calls deliberately stay idle until that profile belongs to the
  // selected organization; then this effect subscribes with the hydrated scope.
  //
  // Deliberately NOT gated on canStartOrgListeners: that is the *initial-start* gate and is only
  // true while the org doc is fetching (getOrganizationSetupState: `fetching === true`), flipping
  // false once it finishes. The org-user rooms doc often resolves AFTER that, so gating a
  // re-subscribe on it would block forever (the intermittent empty list). Admins and
  // parents are not room-scoped and therefore remain no-ops here.
  const userRoomsKey = useMemo(() => (userRooms ? Object.keys(userRooms).sort().join(',') : ''), [userRooms]);
  const isParent = !!userClaims?.parent;
  const isRoomScopedUser = isUsersHighestClaimOneOf(userClaims, [CLAIMS_ENUM.teacher, CLAIMS_ENUM.supportStaff]);
  const isRoomScopeHydrated = userProfileOrganizationId === organizationId;

  useEffect(() => {
    if (!isAuthenticated || !organizationId || isParent || !isRoomScopedUser || !isRoomScopeHydrated) return;

    unsubscribeRefs.current.organizationLocationsListener?.();
    unsubscribeRefs.current.organizationRoomsListener?.();
    unsubscribeRefs.current.organizationStudentsListener?.();

    startListeners.locations(organizationId);
    startListeners.rooms(organizationId);
    startListeners.students(organizationId);
  }, [isAuthenticated, organizationId, isParent, isRoomScopedUser, isRoomScopeHydrated, userRoomsKey, startListeners]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      unsubscribeOrganizationListeners();
      unsubscribeUserListeners();
    };
  }, [unsubscribeOrganizationListeners, unsubscribeUserListeners]);

  // Memoize FileServiceProvider configuration
  const fileServiceConfig = useMemo(
    () => ({
      auth: auth().currentUser as User,
      fileServiceURL: FILE_SERVICE_HOST ?? '',
      organizationId,
    }),
    [organizationId]
  );

  return (
    <FileServiceProvider configuration={fileServiceConfig}>
      <ResponsiveContainer>
        <AppRouter />
      </ResponsiveContainer>
    </FileServiceProvider>
  );
}

// Memoize mapStateToProps selector
const mapStateToProps = (state: RootReducerState) => {
  const currentOrganization = state.organizations.currentOrganization;
  const { organizationId, needsOrgOnboarding, canStartOrgListeners } = getOrganizationSetupState(state.organizations);
  return {
    currentOrganization,
    organizationId,
    needsOrgOnboarding,
    canStartOrgListeners,
    userRooms: state.user.rooms,
    userProfileOrganizationId: state.user.defaultOrganization,
  };
};

const mapAuthContextToProps = ({
  isReady,
  isAuthenticated,
  needsVerification,
  authUser,
  authSession,
  userClaims,
  userStatus,
  forceUserRefresh,
}: IContextType) => {
  return {
    isReady,
    isAuthenticated,
    needsVerification,
    authUser,
    userId: authUser?.uid,
    authSession,
    userClaims,
    userStatus,
    forceUserRefresh,
  };
};

export default WSA.Components.withWSAuth(
  WSA.Components.mapAuthContextToProps(
    mapAuthContextToProps,
    connect<any, any, any, RootReducerState>(mapStateToProps, {
      userSignedIn,
      userSignedOut,
      startUserProfileListener,
      startOrganizationListener,
      startOrganizationPermissionsListener,
      startOrganizationRolesListener,
      startOrganizationLocationsListener,
      startOrganizationRoomsListener,
      startOrganizationStudentsListener,
      startOrganizationStatsListener,
      startOrganizationClockedInListener,
      startOrganizationContactsListener,
    })(App)
  ),
  {
    onSignIn: trackUser,
    onSignOut: trackUserSignout,
    onNewSession: trackNewSession,
  }
);
